import { useMutation, DataProxy } from '@apollo/client';
import { useEffect, useCallback } from 'react';
import { uniqBy } from 'ramda';
import { loadAdyenScript } from '../../../lib/loadScript';
import { encryptCard } from '../../../lib/encryptCard';
import { COUNTRIES } from '../../../constants';
import {
  BillingAddress,
  PaymentMethodsQuery,
  PAYMENT_METHODS_QUERY
} from '../usePaymentMethodsQuery';
import { usePsd2 } from '../usePsd2';
import {
  CREATE_PAYMENT_METHOD_MUTATION,
  CreatePaymentMethodMutation,
  CreatePaymentMethodMutationVariables
} from './createPaymentMethodMutation.rest';

type CreateArgs = {
  cardholderName: string;
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  securityCode: string;
  billingAddress: BillingAddress;
};

const updateCache = (cache?: DataProxy, data?: CreatePaymentMethodMutation | null) => {
  if (!data || !cache) return;

  let cachedData;

  try {
    cachedData = cache.readQuery<PaymentMethodsQuery>({ query: PAYMENT_METHODS_QUERY });
  } catch (error) {
    // if the query is not in the cache, do nothing
  }

  if (!cachedData) return;

  cache.writeQuery<PaymentMethodsQuery>({
    query: PAYMENT_METHODS_QUERY,
    data: {
      paymentMethods: {
        __typename: 'RestPaymentMethods',
        data: uniqBy(paymentMethod => paymentMethod.id, [
          data.createPaymentMethod.data,
          ...cachedData.paymentMethods.data
        ])
      }
    }
  });
};

export const useCreatePaymentMethodMutation = () => {
  const { psd2Params, verifyPsd2 } = usePsd2();

  const [mutate, { loading, error, client }] = useMutation<
    CreatePaymentMethodMutation,
    CreatePaymentMethodMutationVariables
  >(CREATE_PAYMENT_METHOD_MUTATION);

  useEffect(() => {
    loadAdyenScript();
  }, []);

  const handleCreate = useCallback(
    async ({
      cardholderName,
      cardNumber,
      expirationMonth,
      expirationYear,
      securityCode,
      billingAddress
    }: CreateArgs) => {
      const country = COUNTRIES.find(country => country.alpha2Code === billingAddress.countryCode);
      const encryptedCard = encryptCard({
        holderName: cardholderName,
        number: cardNumber,
        expiryMonth: expirationMonth,
        expiryYear: expirationYear,
        cvc: securityCode
      });

      if (!country) throw new Error('createPaymentMethodMutation: country not found');

      const result = await mutate({
        variables: {
          input: {
            data: {
              type: 'payments',
              attributes: {
                ...psd2Params,
                encrypted_data: encryptedCard,
                billing_address: {
                  id: billingAddress.id,
                  country_name: country.name,
                  extended_address: billingAddress.extendedAddress,
                  locality: billingAddress.locality,
                  postal_code: billingAddress.postalCode,
                  region: billingAddress.region,
                  street_address: billingAddress.streetAddress
                }
              }
            }
          }
        }
      });

      const attributes = result.data?.createPaymentMethod.data.attributes;
      await verifyPsd2(attributes?.resultCode, attributes?.psd2AuthData);

      updateCache(client, result.data);

      return result;
    },
    [client, mutate, psd2Params, verifyPsd2]
  );

  return [handleCreate, { loading, error }] as const;
};
